
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';

interface UsePageNavigationOptions {
  pageStorageKey?: string;
  persistFilters?: boolean;
}

// Detalhe = /{recurso}/{id}, onde id é numérico OU UUID (freelancers/empresas usam UUID).
// Uma página de detalhe nunca deve ser destino do "Voltar" — se virar, o botão navega
// para a própria URL e nada acontece.
const DETAIL_PATH_RE = /\/[^/]+\/(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

export function usePageNavigation(options: UsePageNavigationOptions = {}) {
  const { 
    pageStorageKey = 'current_page', 
    persistFilters = true 
  } = options;
  
  const navigate = useNavigate();
  const location = useLocation();

  // Salva a página atual e os parâmetros de URL quando o usuário navega para outra página
  useEffect(() => {
    // Salva o caminho atual e parâmetros para uso posterior
    const currentPath = location.pathname;
    const currentSearch = location.search;
    
    const isDetailPage = DETAIL_PATH_RE.test(currentPath);

    // Só salva se não for uma página de detalhes ou nova entrada
    if (currentPath && !currentPath.includes('/new') && !isDetailPage) {
      // Salva o path atual (ex: /freelancers)
      sessionStorage.setItem(`${pageStorageKey}_path`, currentPath);
      
      // Salva os parâmetros de URL se persistFilters estiver ativado
      if (persistFilters) {
        sessionStorage.setItem(`${pageStorageKey}_search`, currentSearch);
      }
      
      // Se a URL tiver um parâmetro de página, vamos armazenar isso separadamente
      const searchParams = new URLSearchParams(currentSearch);
      const pageParam = searchParams.get('page');
      
      if (pageParam) {
        sessionStorage.setItem(`${pageStorageKey}`, pageParam);
      }
    }
  }, [location, pageStorageKey, persistFilters]);

  // Função para navegar para a página de detalhes preservando o contexto atual
  const navigateWithContext = (to: string, options?: { replace?: boolean }) => {
    // Certifique-se de que 'to' seja apenas o caminho base sem parâmetros de consulta
    const [basePath, queryParams] = to.split('?');
    
    // Adicione o parâmetro fromDetails
    const searchParams = new URLSearchParams(queryParams || '');
    searchParams.set('fromDetails', 'true');
    
    // Construa a URL final com os parâmetros
    const finalUrl = `${basePath}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    
    navigate(finalUrl, options);
  };

  // Função para retornar à última página visitada.
  // `fallbackPath` é o destino quando não há nem caminho salvo nem histórico
  // de navegação dentro do app (acesso direto à URL, nova aba, refresh na
  // própria tela de detalhe). Antes caía em `navigate(-1)` com pilha vazia, o
  // que não fazia nada — o botão "Voltar" parecia quebrado.
  const navigateBack = (fallbackPath: string = '/') => {
    // Se tiver uma página anterior salva, vamos reconstruir a URL
    const savedPath = sessionStorage.getItem(`${pageStorageKey}_path`);
    const savedSearch = sessionStorage.getItem(`${pageStorageKey}_search`) || '';
    const savedPage = sessionStorage.getItem(`${pageStorageKey}`);

    // Defesa contra savedPath inválido: navegar para a própria URL atual é um
    // no-op (o botão "Voltar" não fazia nada), e uma página de detalhe nunca é
    // um destino de volta válido. Isso acontece com resíduo de sessionStorage
    // gravado por versões antigas (antes do regex de UUID): o valor podre nunca
    // era sobrescrito porque só páginas não-detalhe sobrescrevem. Limpa e cai no
    // fallback de histórico.
    const savedPathIsUsable =
      savedPath && savedPath !== location.pathname && !DETAIL_PATH_RE.test(savedPath);

    if (!savedPathIsUsable && savedPath) {
      sessionStorage.removeItem(`${pageStorageKey}_path`);
      sessionStorage.removeItem(`${pageStorageKey}_search`);
    }

    if (savedPathIsUsable) {
      // Se tiver uma página salva e não estiver nos parâmetros, adicionamos
      if (savedPage && !savedSearch.includes(`page=${savedPage}`)) {
        const searchParams = new URLSearchParams(savedSearch);
        searchParams.set('page', savedPage);
        navigate(`${savedPath}?${searchParams.toString()}`);
        return;
      }
      
      // Se tiver parâmetros de pesquisa, usamos eles
      if (savedSearch) {
        navigate(`${savedPath}${savedSearch}`);
        return;
      }
      
      // Caso contrário, só navegamos para o caminho salvo
      navigate(savedPath);
      return;
    }
    
    // Sem caminho salvo: só usamos o histórico nativo se houver uma entrada
    // anterior DENTRO do app. O React Router guarda o índice da entrada atual
    // em `window.history.state.idx`; idx === 0 significa que esta é a primeira
    // entrada (não há para onde voltar) — nesse caso vamos pra rota padrão.
    const historyState = window.history.state as { idx?: number } | null;
    const idx = typeof historyState?.idx === 'number' ? historyState.idx : 0;
    if (idx > 0) {
      navigate(-1);
    } else {
      navigate(fallbackPath);
    }
  };

  return {
    navigateWithContext,
    navigateBack
  };
}
